import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy,
  QueryList, TemplateRef, ViewChildren, ViewEncapsulation } from '@angular/core';
import ResizeObserver from 'resize-observer-polyfill';

export const debounce = (cb: (...args) => any, minInterval: number = 1000) => {
  let timeout: any;
  return (...args) => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(() => cb(...args), minInterval);
  };
};

@Component({
  selector: 'gen-v-scroller',
  templateUrl: './v-scroller.component.html',
  styleUrls: ['./v-scroller.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VScrollerComponent<RowType> implements OnDestroy, AfterViewInit {
  private $viewport: HTMLElement;

  readonly debouncedScrollStop: () => void = undefined;
  private debouncedScroll: () => void = undefined;
  private rowsOverflowInterval;
  private viewPortResizeObserver: ResizeObserver;

  private sourceRows: RowType[] = [];

  private currentScrollPos = 0;
  public rowsDynamicHeights: { [idx: string]: number} = { };
  public lastCalculatedMeanRowHeight: number;

  public visibleIds: {
    firstVisibleIdx?: 0,
    lastVisibleIdx?: 0,
    sumHeight?: number,
  } = { };

  // disabled - turn back to the origin scroll behaviour
  @Input()
  public disabled: boolean = false;

  @Input()
  public rowTemplate: TemplateRef<any>;

  @Input()
  public defaultRowHeight: number = 200;

  @Input()
  private aheadCount: number = 3;

  @Input()
  public set rows(rows: RowType[]) {
    this.sourceRows = rows;
    this.rowsDynamicHeights = { };
  }

  public get rows(): RowType[] {
    return this.sourceRows;
  }

  @ViewChildren('vVisibleRow') $vRows: QueryList<ElementRef>;

  constructor(
    private elRef: ElementRef,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this.debouncedScrollStop = debounce(this.onViewportScrollStop.bind(this), 300);
    this.lastCalculatedMeanRowHeight = this.defaultRowHeight;
  }

  ngAfterViewInit() {
    this.$viewport = this.elRef.nativeElement.children[0];

    const debouncedResize = debounce(this.onViewportResize.bind(this), 200);
    this.debouncedScroll = debounce(this.onViewportScroll.bind(this), 200);

    this.ngZone.runOutsideAngular(() => {
      this.$viewport.addEventListener('scroll', this.debouncedScroll);

      this.viewPortResizeObserver = new ResizeObserver(() => debouncedResize());
      this.viewPortResizeObserver.observe(this.$viewport);

      this.rowsOverflowInterval = setInterval(() => this.refreshRowsHeight(), 1000);

      setTimeout(() => this.refreshVisibility());
    });
  }

  ngOnDestroy() {
    this.ngZone.runOutsideAngular(() => {
      clearInterval(this.rowsOverflowInterval);
      this.viewPortResizeObserver.unobserve(this.$viewport);
      this.$viewport.removeEventListener('scroll', this.debouncedScroll);
    });
  }

  private onViewportScroll() {
    this.ngZone.runOutsideAngular(() => {
      this.debouncedScrollStop();
    });
    this.currentScrollPos = this.$viewport.scrollTop;
  }

  private onViewportScrollStop() {
    if (this.currentScrollPos === this.$viewport.scrollTop) {
      this.refreshVisibility();
    }
  }

  private onViewportResize() {
    if (!this.$viewport.scrollTop) {
      this.refreshVisibility();
    }
  }

  // determine visible indexes
  private refreshVisibility() {
    if (this.$viewport.offsetHeight) {
      const topVisiblePoint = this.$viewport.scrollTop;
      const bottomVisiblePoint = this.$viewport.scrollTop + this.$viewport.offsetHeight;

      this.visibleIds = this.rows.reduce((accum, row, idx) => {
        if (accum.sumHeight <= topVisiblePoint) {
          accum.firstVisibleIdx = Math.max(0, idx - this.aheadCount);
        }
        if (accum.sumHeight <= bottomVisiblePoint) {
          accum.lastVisibleIdx = Math.min(idx + this.aheadCount, this.rows.length - 1);
        }

        accum.sumHeight += this.rowsDynamicHeights[idx] || this.defaultRowHeight;
        return accum;
      }, {
        firstVisibleIdx: null,
        lastVisibleIdx: null,
        sumHeight: 0,
      });

      this.cd.markForCheck();
    }
  }

  // recalculate visible rows height
  private refreshRowsHeight() {
    const vRows = this.$vRows.toArray();

    const meanCalculate = vRows.reduce((accum, $vRow) => {
      const $rowContent = $vRow.nativeElement.children[0];
      const realIdx = Number($vRow.nativeElement.getAttribute('data-real-idx'));
      if ($rowContent?.offsetHeight && (typeof realIdx !== 'undefined')) {
        if (this.rowsDynamicHeights[realIdx] !== ($rowContent.offsetHeight + 2)) {
          accum.heightUpdated = true;
        }
        this.rowsDynamicHeights[realIdx] = ($rowContent.offsetHeight + 2);
        accum.count++;
        accum.height += this.rowsDynamicHeights[realIdx];
      }

      return accum;
    }, {
      heightUpdated: false,
      height: 0,
      count: 0,
    });

    if (meanCalculate.heightUpdated) {
      if (meanCalculate.count) {
        this.lastCalculatedMeanRowHeight = Math.ceil(meanCalculate.height / meanCalculate.count);
      }
      this.cd.markForCheck();
    }
  }

}
